// src/services/queries/tokens/tokensScreenerQueries.ts
import { API_BASE_URL } from "@/services";

// Shapes mirror src/types/dashboards/dashboardCurrent.ts
export interface ScreenerTokenCurrent {
  token_id: number;
  ticker: string;
  name?: string | null | undefined;
  color?: string | null | undefined;
  logo_url?: string | null | undefined;
  metrics: Record<string, number | null>;
}

export interface ScreenerCurrentValuesResponse {
  tokens: ScreenerTokenCurrent[];
  lastUpdated?: string;
}

export interface ScreenerMetricWindowValue { value?: number | null; [k: string]: unknown }
export interface ScreenerMetricWindows { metric_key: string; windows?: Record<string, ScreenerMetricWindowValue> }
export interface ScreenerTokenWindows {
  token_id: number;
  ticker: string;
  name?: string | null | undefined;
  color?: string | null | undefined;
  logo_url?: string | null | undefined;
  metrics: ScreenerMetricWindows[];
}
export interface ScreenerWindowsResponse {
  tokens: ScreenerTokenWindows[];
  lastUpdated?: string;
}

function qs(params: Record<string, unknown>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export async function fetchTokensCurrent(params: {
  tickersCsv?: string;
  metricKeysCsv?: string;
  marketCapMin?: number;
  marketCapMax?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<ScreenerCurrentValuesResponse> {
  const url = `${API_BASE_URL}/tokens/current${qs({
    tickers: params.tickersCsv,
    metric_keys: params.metricKeysCsv,
    market_cap_min: params.marketCapMin,
    market_cap_max: params.marketCapMax,
    sort_by: params.sortBy ?? "market_cap",
    sort_order: params.sortOrder ?? "desc",
    limit: params.limit,
    offset: params.offset,
  })}`;
  const res = await fetch(url, params.signal ? { signal: params.signal } : {});
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const list = (await res.json()) as ScreenerTokenCurrent[];
  return { tokens: list };
}

export async function fetchTokensCurrentRatios(params: {
  tickersCsv?: string;
  ratioKeysCsv?: string;
  marketCapMin?: number;
  marketCapMax?: number;
  sortBy?: string | null;
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<ScreenerCurrentValuesResponse> {
  const url = `${API_BASE_URL}/tokens/ratios${qs({
    tickers: params.tickersCsv,
    ratio_keys: params.ratioKeysCsv,
    market_cap_min: params.marketCapMin,
    market_cap_max: params.marketCapMax,
    sort_by: params.sortBy ?? undefined,
    sort_order: params.sortOrder ?? "desc",
    limit: params.limit,
    offset: params.offset,
  })}`;
  const res = await fetch(url, params.signal ? { signal: params.signal } : {});
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const list = (await res.json()) as ScreenerTokenCurrent[];
  return { tokens: list };
}

export async function fetchTokensRollups(params: {
  tickersCsv?: string;
  metricKeysCsv?: string;
  windowsCsv?: string; // e.g. "1d,7d,30d,60d,90d,180d,365d"
  marketCapMin?: number;
  marketCapMax?: number;
  sortBy?: string;
  sortWindow?: string | null;
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<ScreenerWindowsResponse> {
  const url = `${API_BASE_URL}/tokens/rollups${qs({
    tickers: params.tickersCsv,
    metric_keys: params.metricKeysCsv,
    windows: params.windowsCsv,
    market_cap_min: params.marketCapMin,
    market_cap_max: params.marketCapMax,
    sort_by: params.sortBy ?? "market_cap",
    sort_window: params.sortWindow ?? undefined,
    sort_order: params.sortOrder ?? "desc",
    limit: params.limit,
    offset: params.offset,
  })}`;
  const res = await fetch(url, params.signal ? { signal: params.signal } : {});
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const list = (await res.json()) as ScreenerTokenWindows[];
  return { tokens: list };
}

export async function fetchTokensChanges(params: {
  tickersCsv?: string;
  metricKeysCsv?: string;
  windowsCsv?: string;
  marketCapMin?: number;
  marketCapMax?: number;
  sortBy?: string;
  sortWindow?: string | null;
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<ScreenerWindowsResponse> {
  const url = `${API_BASE_URL}/tokens/changes${qs({
    tickers: params.tickersCsv,
    metric_keys: params.metricKeysCsv,
    windows: params.windowsCsv,
    market_cap_min: params.marketCapMin,
    market_cap_max: params.marketCapMax,
    sort_by: params.sortBy ?? "market_cap",
    sort_window: params.sortWindow ?? undefined,
    sort_order: params.sortOrder ?? "desc",
    limit: params.limit,
    offset: params.offset,
  })}`;
  const res = await fetch(url, params.signal ? { signal: params.signal } : {});
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const list = (await res.json()) as ScreenerTokenWindows[];
  return { tokens: list };
}

// Multi-ticker history response type
export interface TokenHistoryResponse {
  ticker: string;
  metrics: Array<{ metric_key: string; dates: Array<string | number>; values: Array<number | null> }>;
}

/**
 * Fetch historical non-ratio metrics for multiple tokens
 */
export async function fetchTokensHistoryPoints(params: {
  tickersCsv: string; // comma-separated tickers
  metricKeysCsv?: string; // comma-separated metric keys
  signal?: AbortSignal;
}): Promise<TokenHistoryResponse[]> {
  const url = `${API_BASE_URL}/tokens/history/points${qs({
    tickers: params.tickersCsv,
    metric_keys: params.metricKeysCsv,
  })}`;
  const res = await fetch(url, params.signal ? { signal: params.signal } : {});
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Fetch historical ratio metrics for multiple tokens
 */
export async function fetchTokensHistoryRatios(params: {
  tickersCsv: string; // comma-separated tickers
  ratioKeysCsv?: string; // comma-separated ratio keys
  signal?: AbortSignal;
}): Promise<TokenHistoryResponse[]> {
  const url = `${API_BASE_URL}/tokens/history/ratios${qs({
    tickers: params.tickersCsv,
    ratio_keys: params.ratioKeysCsv,
  })}`;
  const res = await fetch(url, params.signal ? { signal: params.signal } : {});
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchTokenCategoryMap(params: {
  tickersCsv?: string;
  signal?: AbortSignal;
}): Promise<Record<string, number[]>> {
  const url = `${API_BASE_URL}/tokens/category-map${qs({ tickers: params.tickersCsv })}`;
  const res = await fetch(url, params.signal ? { signal: params.signal } : {});
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

